(ns machtfit.stepchall.join-team
  (:require [machtfit.core :as machtfit]
            [machtfit.language :as language]
            [machtfit.startpage.api-events :as api-events]
            [machtfit.stepchall.api :as api]
            [machtfit.stepchall.create-team :as create-team]
            [machtfit.stepchall.icons :as icons]
            [machtfit.stepchall.modal :as modal]
            [machtfit.stepchall.share-team :as share-team]
            [machtfit.stepchall.subs :as subs]
            [machtfit.translation :refer [tr tr-with-place-holders t]]
            [machtfit.widgets.box :as box]
            [machtfit.widgets.icon :as icon]
            [medley.core :refer [map-vals]]
            [re-frame.core :as rf]))

(rf/reg-sub ::team-to-join
  :<- [:get [:ui :page :team-id]]
  :<- [::subs/teams-by-id]

  (fn [[id teams] _]
    (teams id)))

(rf/reg-sub ::team-to-join-member-count
  :<- [::team-to-join]

  (fn [team-to-join _]
    (:member-count team-to-join)))

(rf/reg-sub ::team-is-full?
  :<- [::team-to-join]
  :<- [:get [:stepchall :max-team-size]]

  (fn [[team max-team-size] _]
    (and (some? max-team-size)
         (>= (:member-count team) max-team-size))))

(rf/reg-event-fx ::join-team-clicked
  (fn [{:keys [db]} _]
    (let [nickname (-> db :stepchall :participant :nickname)
          access-code (-> db :ui :stepchall :team-form :access-code)
          id (-> db :ui :page :team-id)
          team-name (-> db
                        :stepchall
                        :teams
                        (->> (group-by :id)
                             (map-vals first))
                        (get id)
                        :name
                        :de)]
      {:post (api/request db
                          {:function :stepchall-join-team
                           :params {:team_name team-name
                                    :access_code access-code
                                    :nickname nickname}})})))

(defn- team-full-infobox []
  [box/info
   {:icon [:div.flex.w-10.h-10.justify-center.items-center.rounded-full.bg-warning-50.text-brand-500
           {:class "p-2.5"}
           [icon/outline
            "info-circle"
            {:class "text-warning-500"
             :width "h-5"
             :height "w-5"}]]
    :content [:div.flex.flex-col.gap-2
              [:strong
               (t
                {:de "Team voll"
                 :en "Team full"})]
              [:p
               (t
                {:de "Dieses Team ist bereits vollständig. Du kannst ganz einfach ein eigenes Team gründen oder dich einem anderen Team anschließen, das die maximale Anzahl noch nicht erreicht hat."
                 :en "This team is already complete. You can simply create your own team or join another existing team that has not yet reached the maximum number."})]]}])

(defn- join-team-button []
  (let [member-count @(rf/subscribe [::team-to-join-member-count])
        team-is-full? @(rf/subscribe [::team-is-full?])]
    [create-team/button
     {:on-click #(when-not team-is-full? (rf/dispatch [::join-team-clicked %]))
      :disabled? team-is-full?
      :text (if team-is-full?
              (t
               {:de "Team voll"
                :en "Team full"})
              (tr-with-place-holders "{member-count} Mitglied werden"
                                     {"member-count" (language/ordinal (inc member-count))}))}]))

(defn- team-members []
  (if-let [members @(rf/subscribe [::subs/members-for-team])]
    (into [:div.sc_team-members]
          (map (fn [member] [:div.sc_team-members-single
                             [:div.name (:nickname member) (when (:team-founder? member)
                                                             [:span.sc_team-founder
                                                              (t {:de "Gründer:in"
                                                                  :en "Founder"})])]]))
          (sort-by :nickname members))
    (into [:div.sc_team-members]
          (repeat
           (get-in @(rf/subscribe [::subs/teams-by-id]) [@(rf/subscribe [:get [:ui :page :team-id]]) :member-count])
           [:div.sc_team-members-single
            [:div.name [:span.content-placeholder]]]))))

(defn- join-team []
  (let [{:keys [member-count name color requires-access-code?]} @(rf/subscribe [::team-to-join])]
    [:div.sc
     [:div.sc_join-team
      [:div.sc_container
       [:div.sc_back {:on-click #(js/history.back)} [:i.sc_icon [icons/back]] (tr "zurück")]
       [:h2 (tr "Team beitreten")]
       [:div.sc_input-group
        [:label.sc_input-label (tr "Team")]
        [:div.sc_vertical-center
         [:div.sc_team-item-color {:style {:background-color color}}]
         [:div.sc_team-item-name (t name)]]]
       [create-team/nickname-input]
       (when requires-access-code?
         [:div
          (tr "Frag ein Teammitglied nach dem Zugangscode.")
          [create-team/access-code-input]])
       [create-team/team-privacy-box]
       [:div.sc_input-group
        [:label.sc_input-label (str (t {:de "Mitglieder"
                                        :en "Members"})
                                    " (" member-count ")")]
        [team-members]]
       (when
        (or @(rf/subscribe [::team-is-full?])
            (= @(rf/subscribe [:get [:ui :page :error :name]]) :team-full))
         [:div.sc_container
          [team-full-infobox]])
       [:div.sc_container {:style {:visibility "hidden"}}
        [join-team-button {:member-count member-count
                           :team-name name}]]
       [:div.sticky-button-bottom
        [:div.sc_container
         [join-team-button {:member-count member-count
                            :team-name name}]]]]]]))

(defn- team-joined-successfully []
  [modal/modal {:img-src (machtfit/static "img/stepchall/joined-team.svg")
                :title (tr "Willkommen im Team!")
                :content [:<> (tr-with-place-holders
                               [:p "Herzlichen Glückwunsch! Du bist jetzt im Team " [:b [:q "{team-name}"]]
                                ". Lade am besten gleich weitere Kolleg:innen ein. Wir wünschen dir viel Spaß!"]
                               (create-team/place-holders))
                          [share-team/copy-link]
                          [share-team/whatsapp-link]
                          [share-team/email-link]]
                :button-group [{:text (tr "Los geht's!")
                                :page {:id :stepchall}}]
                :close-button {:page {:id :stepchall}}}])

(defn join-team-page []
  [:<>
   [join-team]
   (when @(rf/subscribe [::subs/is-team-member?]) [team-joined-successfully])])

(rf/reg-event-fx ::navigate-to-stepchall-join-team
  (fn [{:keys [db]} [_ {:keys [team-id team-access-code]}]]
    (-> {:db db}
        (assoc-in [:db :ui :page] {:id :stepchall
                                   :subpage :join-team
                                   :team-id team-id})
        (assoc-in [:db :ui :stepchall :team-form :access-code] team-access-code)
        (assoc :scroll-to {:frame :app-frame
                           :mode :page-top})
        (api-events/api-requests-on-stepchall-event))))
